import type {
  ApplyLoaderRevBody,
  CreateLoaderBody,
  CreateLoaderRevBody,
  ListLoaderRevLoadersResponse,
  ListLoaderRevsResponse,
  ListLoadersResponse,
  LoaderDetailDto,
  LoaderMatrixResponse,
  LoaderQualityResponse,
  LoaderRevDto,
  LoaderSummaryDto,
  NextLoaderSerialResponse,
  RobotLoaderResponse,
  SwapRobotLoaderBody,
  UpdateLoaderBody,
  UpdateLoaderRevBody,
} from '@bhops/shared';
import { apiClient } from './client';

// 로더(로봇 팔) 이력·역추적 — docs/plan/loader-history-traceability-2026-07.

// 개선 9건 차수2(26-08-04) 결함③ — 차수1 이 병렬 충돌을 피하려고 두었던 지역 확장
// (UpdateLoaderPatch / ListLoaders / deletedAt 교차타입)을 지웠다. `serial` 과
// `deletedAt` 은 이제 shared 정본(UpdateLoaderBody · LoaderSummaryDto)에 있으므로
// wire 계약이 1벌이고, 서버가 필드를 바꾸면 콘솔이 컴파일 단계에서 안다.
//
// 아래 두 이름은 **정본을 그대로 다시 내보내는 것뿐**이다(필드 선언이 없으니 어긋날
// 여지도 없다). 화면 파일들이 이 이름으로 import 하고 있어 남겨 둔다 — 화면 쪽
// import 를 shared 로 바꾸는 정리는 로더 화면을 만지는 차수에서 함께 한다.
export type LoaderSummary = LoaderSummaryDto;
export type LoaderDetail = LoaderDetailDto;

export interface ListLoadersParams {
  q?: string;
  status?: string;
  active?: 'all';
  /** 개선 9건 차수1 — 입고검사 판정('조건부합격' 포함). */
  inspection?: string;
}

export const loadersApi = {
  async list(params: ListLoadersParams = {}): Promise<ListLoadersResponse> {
    const { data } = await apiClient.get<ListLoadersResponse>('/loaders', {
      params: {
        ...(params.q ? { q: params.q } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.active ? { active: params.active } : {}),
        ...(params.inspection ? { inspection: params.inspection } : {}),
      },
    });
    return data;
  },

  async get(id: string): Promise<LoaderDetail> {
    const { data } = await apiClient.get<LoaderDetail>(`/loaders/${encodeURIComponent(id)}`);
    return data;
  },

  async create(body: CreateLoaderBody): Promise<LoaderSummary> {
    const { data } = await apiClient.post<LoaderSummary>('/loaders', body);
    return data;
  },

  async update(id: string, body: UpdateLoaderBody): Promise<LoaderSummary> {
    const { data } = await apiClient.patch<LoaderSummary>(
      `/loaders/${encodeURIComponent(id)}`,
      body,
    );
    return data;
  },

  async remove(id: string): Promise<LoaderSummary> {
    const { data } = await apiClient.delete<LoaderSummary>(`/loaders/${encodeURIComponent(id)}`);
    return data;
  },

  // 개선 9건 차수1(26-08-04) — 소프트 삭제 복구(ADMIN). 실수 삭제를 DB 손대지 않고
  // 되돌리는 유일한 길.
  async restore(id: string): Promise<LoaderSummary> {
    const { data } = await apiClient.post<LoaderSummary>(
      `/loaders/${encodeURIComponent(id)}/restore`,
    );
    return data;
  },

  // 개선 9건 차수1(26-08-04) — 완전삭제(ADMIN). 삭제 선행 + 이력 0건일 때만
  // (서버 가드). 되돌릴 수 없다.
  async hardDelete(id: string): Promise<{ id: string; deleted: true }> {
    const { data } = await apiClient.delete<{ id: string; deleted: true }>(
      `/loaders/${encodeURIComponent(id)}/permanent`,
    );
    return data;
  },

  async quality(): Promise<LoaderQualityResponse> {
    const { data } = await apiClient.get<LoaderQualityResponse>('/loaders/quality');
    return data;
  },

  async nextSerial(
    productionYm: string,
    productionKind?: string,
  ): Promise<NextLoaderSerialResponse> {
    const { data } = await apiClient.get<NextLoaderSerialResponse>('/loaders/next-serial', {
      params: { productionYm, ...(productionKind ? { productionKind } : {}) },
    });
    return data;
  },
};

// 로봇 상세 '로더' 탭 (robots/:robotId/loader).
export const robotLoaderApi = {
  async get(robotId: string): Promise<RobotLoaderResponse> {
    const { data } = await apiClient.get<RobotLoaderResponse>(
      `/robots/${encodeURIComponent(robotId)}/loader`,
    );
    return data;
  },

  async swap(robotId: string, body: SwapRobotLoaderBody): Promise<RobotLoaderResponse> {
    const { data } = await apiClient.post<RobotLoaderResponse>(
      `/robots/${encodeURIComponent(robotId)}/loader`,
      body,
    );
    return data;
  },

  async detach(robotId: string, body: SwapRobotLoaderBody): Promise<RobotLoaderResponse> {
    const { data } = await apiClient.post<RobotLoaderResponse>(
      `/robots/${encodeURIComponent(robotId)}/loader/detach`,
      body,
    );
    return data;
  },
};

// 로더 개조(로더판 ECN).
export const loaderRevsApi = {
  async list(active?: 'all'): Promise<ListLoaderRevsResponse> {
    const { data } = await apiClient.get<ListLoaderRevsResponse>('/loader-revs', {
      params: active ? { active } : {},
    });
    return data;
  },

  async get(id: string): Promise<LoaderRevDto> {
    const { data } = await apiClient.get<LoaderRevDto>(`/loader-revs/${encodeURIComponent(id)}`);
    return data;
  },

  async matrix(): Promise<LoaderMatrixResponse> {
    const { data } = await apiClient.get<LoaderMatrixResponse>('/loader-revs/matrix');
    return data;
  },

  async listLoaders(id: string): Promise<ListLoaderRevLoadersResponse> {
    const { data } = await apiClient.get<ListLoaderRevLoadersResponse>(
      `/loader-revs/${encodeURIComponent(id)}/loaders`,
    );
    return data;
  },

  async create(body: CreateLoaderRevBody): Promise<LoaderRevDto> {
    const { data } = await apiClient.post<LoaderRevDto>('/loader-revs', body);
    return data;
  },

  async update(id: string, body: UpdateLoaderRevBody): Promise<LoaderRevDto> {
    const { data } = await apiClient.patch<LoaderRevDto>(
      `/loader-revs/${encodeURIComponent(id)}`,
      body,
    );
    return data;
  },

  async remove(id: string): Promise<LoaderRevDto> {
    const { data } = await apiClient.delete<LoaderRevDto>(`/loader-revs/${encodeURIComponent(id)}`);
    return data;
  },

  // 완전삭제 — 보관된 개조만, 적용 이력 0건일 때만(서버 가드). 되돌릴 수 없다.
  async hardDelete(id: string): Promise<{ id: string; deleted: true }> {
    const { data } = await apiClient.delete<{ id: string; deleted: true }>(
      `/loader-revs/${encodeURIComponent(id)}/permanent`,
    );
    return data;
  },

  async apply(id: string, body: ApplyLoaderRevBody): Promise<ListLoaderRevLoadersResponse> {
    const { data } = await apiClient.post<ListLoaderRevLoadersResponse>(
      `/loader-revs/${encodeURIComponent(id)}/apply`,
      body,
    );
    return data;
  },

  async unapply(id: string, loaderUnitId: string): Promise<ListLoaderRevLoadersResponse> {
    const { data } = await apiClient.delete<ListLoaderRevLoadersResponse>(
      `/loader-revs/${encodeURIComponent(id)}/loaders/${encodeURIComponent(loaderUnitId)}`,
    );
    return data;
  },
};
